import {
  DefinedUseQueryResult,
  QueryKey,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { useCallback, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { showNotification } from '@shared/components/notification';
import { getDefaultError } from '@shared/utils/common';
import { AnyErrorResponse, queryClient } from '@shared/utils/http-client';

export type AxiosRequestCallback<TPayload, TResponse> = (
  payload: TPayload,
  config?: AxiosRequestConfig
) => Promise<AxiosResponse<TResponse>>;

export type AxiosMutationOptions<TResponse = unknown, TPayload = unknown> = Omit<
  UseMutationOptions<TResponse, Error | AxiosError<AnyErrorResponse>, TPayload>,
  'onError'
> & {
  onAxiosError?: (error: AxiosError<AnyErrorResponse>) => void;
  onError?: (error: Error) => void;
};

export type AxiosMutationResult<TResponse = unknown, TPayload = unknown> = UseMutationResult<
  TResponse,
  Error | AxiosError<AnyErrorResponse>,
  TPayload
>;

export const useAxiosMutation = <TPayload = unknown, TResponse = unknown>(
  callback: AxiosRequestCallback<TPayload, TResponse>,
  options?: AxiosMutationOptions<TResponse, TPayload>
): AxiosMutationResult<TResponse, TPayload> => {
  return {
    ...useMutation<TResponse, Error | AxiosError<AnyErrorResponse>, TPayload>({
      ...options,
      mutationFn: async (payload) => (await callback(payload)).data,
      onError: (data) => {
        if (axios.isAxiosError(data)) {
          const axiosError = data as AxiosError<AnyErrorResponse>;
          if (options?.onAxiosError) {
            options.onAxiosError(axiosError);
          } else {
            showNotification(getDefaultError(axiosError.response?.data?.message), 'error');
          }
        } else {
          const error = data as Error;

          if (options?.onError) {
            options.onError(error);
          } else {
            console.error(error);
            showNotification(getDefaultError(), 'error');
          }
        }
      },
    }),
  };
};

export type AxiosQueryOptions<TResponse = unknown> = Omit<
  UseQueryOptions<TResponse, Error | AxiosError<AnyErrorResponse>>,
  'onError'
> & {
  onAxiosError?: (error: AxiosError<AnyErrorResponse>) => void;
  onError?: (error: Error) => void;
};

export type AxiosQueryUpdateCache<TResponse = unknown> = (callback: (data: TResponse) => TResponse) => void;

export type AxiosQueryResult<TResponse = unknown> = UseQueryResult<TResponse, Error | AxiosError<AnyErrorResponse>> & {
  clearCache: () => void;
  updateCache: AxiosQueryUpdateCache<TResponse>;
};

export type DefinedAxiosQueryResult<TResponse = unknown> = DefinedUseQueryResult<
  TResponse,
  Error | AxiosError<AnyErrorResponse>
> & {
  clearCache: () => void;
  updateCache: AxiosQueryUpdateCache<TResponse>;
};

export const useAxiosQuery = <TPayload = unknown, TResponse = unknown>(
  payload: TPayload,
  callback: AxiosRequestCallback<TPayload, TResponse>,
  options?: AxiosQueryOptions<TResponse>
): AxiosQueryResult<TResponse> => {
  const [queryKeys, setQueryKeys] = useState<QueryKey[]>([]);

  const coreQueryKey = useMemo(() => options?.queryKey ?? [uuidv4()], [options?.queryKey]);

  const queryKey = useMemo(() => [...coreQueryKey, payload], [coreQueryKey, payload]);

  const query = useQuery<TResponse, Error | AxiosError<AnyErrorResponse>>({
    ...options,
    queryFn: async ({ signal }) => (await callback(payload, { signal })).data,
    queryKey,
    onError: (data) => {
      if (axios.isAxiosError(data)) {
        const axiosError = data as AxiosError<AnyErrorResponse>;
        if (options?.onAxiosError) {
          options.onAxiosError(axiosError);
        } else {
          showNotification(getDefaultError(axiosError.response?.data?.message), 'error');
        }
      } else {
        const error = data as Error;

        if (options?.onError) {
          options.onError(error);
        } else {
          console.error(error);
          showNotification(getDefaultError(), 'error');
        }
      }
    },
    onSettled: () => {
      setQueryKeys((prev) => [...prev, queryKey]);
    },
  });

  const clearCache = useCallback(() => {
    queryKeys.forEach((key) => queryClient.removeQueries(key));
    setQueryKeys([]);
  }, [queryKeys]);

  const updateCache = useCallback<AxiosQueryUpdateCache<TResponse>>(
    (callback) => {
      queryClient.setQueryData<TResponse>(queryKey, (cache) => (cache ? callback(cache) : cache));
    },
    [queryKey]
  );

  return {
    ...query,
    clearCache,
    updateCache,
  };
};
